import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Address } from '@pineapple/address-search';
import { RootState } from '../../app/store';

// The address as the search widget or a ?locationId= link gives it.
// gnafId is the picked suggestion's G-NAF id, which the Community Fibre
// qualification keys on: @pineapple/address-search sets it from 2.3.0
// (where it is part of Address; the extension here is for the 2.2.1 build
// and can go with the bump), older builds leave it undefined and the
// qualification goes by address parts instead.
export type StoredAddress = Address & { gnafId?: string };

export const emptyAddress = {} as StoredAddress;

export interface AddressState {
  value: StoredAddress;
  status: 'idle' | 'loading' | 'failed';
}

const initialState: AddressState = {
  value: emptyAddress,
  status: 'idle',
};

export const addressSlice = createSlice({
  name: 'address',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    // Use the PayloadAction type to declare the contents of `action.payload`
    setAddressFromUrl: (state, action: PayloadAction<StoredAddress>) => {
      state.value = action.payload;
    },
    setAddress: (state, action: PayloadAction<StoredAddress>) => {
      state.value = action.payload;
    },
    // The unit auto-fills from the address search and can be corrected on
    // the connection-details step, which writes it back here. A changed
    // unit makes the picked suggestion's G-NAF id stale (it named the
    // building, or another unit), so it is dropped rather than carried on
    // to something that keys on it.
    setUnit: (state, action: PayloadAction<string>) => {
      if (action.payload !== state.value.unit) {
        delete state.value.gnafId;
      }
      state.value.unit = action.payload;
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: {},
});

export const { setAddress, setAddressFromUrl, setUnit } = addressSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectAddress = (state: RootState) => state.address.value;
export const selectAddrStatus = (state: RootState) => state.address.status;
export default addressSlice.reducer;
